import { useState } from 'react';
import { Star, Trash2, Plus } from 'lucide-react';
import type { ProjectMilestone } from '@t3/shared';
import { ApiError, apiFetch } from '../api/client.ts';
import { Button } from './Button.tsx';

interface Props {
  projectId: string;
  milestones: ProjectMilestone[];
  onChange: () => Promise<void> | void;
}

export function MilestoneList({ projectId, milestones, onChange }: Props): React.ReactElement {
  const [newTitle, setNewTitle] = useState('');
  const [newDate, setNewDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function add() {
    if (!newTitle.trim() || !newDate) return;
    setBusy(true);
    setErr(null);
    try {
      await apiFetch(`/api/projects/${projectId}/milestones`, {
        method: 'POST',
        json: {
          title: newTitle.trim(),
          milestoneDate: newDate,
        },
      });
      setNewTitle('');
      setNewDate('');
      await onChange();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Failed to add milestone.');
    } finally {
      setBusy(false);
    }
  }

  async function toggle(m: ProjectMilestone) {
    setBusy(true);
    setErr(null);
    try {
      await apiFetch(`/api/projects/${projectId}/milestones/${m.id}`, {
        method: 'PATCH',
        json: { completed: m.completedAt === null },
      });
      await onChange();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Failed to update milestone.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(m: ProjectMilestone) {
    if (!window.confirm(`Delete milestone "${m.title}"?`)) return;
    setBusy(true);
    setErr(null);
    try {
      await apiFetch(`/api/projects/${projectId}/milestones/${m.id}`, { method: 'DELETE' });
      await onChange();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Failed to delete milestone.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <p className="text-xs text-[var(--color-muted)]">
        High-level project markers. Each milestone shows on the team calendar.
      </p>

      {milestones.length === 0 ? (
        <p className="mt-4 text-sm text-[var(--color-muted)]">No milestones yet.</p>
      ) : (
        <ul className="mt-4 space-y-1">
          {milestones.map((m) => {
            const overdue =
              m.completedAt === null && new Date(m.milestoneDate) < new Date(new Date().toDateString());
            return (
              <li
                key={m.id}
                className="group flex items-center gap-2 rounded-[var(--radius-button)] px-2 py-1.5 hover:bg-[var(--color-surface)]"
              >
                <input
                  type="checkbox"
                  checked={m.completedAt !== null}
                  onChange={() => void toggle(m)}
                  disabled={busy}
                  className="h-4 w-4 cursor-pointer accent-amber-600"
                />
                <Star
                  className={
                    'h-3.5 w-3.5 ' +
                    (m.completedAt !== null ? 'text-[var(--color-muted)]' : 'text-amber-600')
                  }
                />
                <span
                  className={
                    'flex-1 text-sm ' +
                    (m.completedAt
                      ? 'text-[var(--color-muted)] line-through'
                      : 'font-medium text-[var(--color-body)]')
                  }
                >
                  {m.title}
                </span>
                <span
                  className={
                    'inline-flex items-center gap-1 text-xs ' +
                    (overdue ? 'text-[var(--color-danger)]' : 'text-[var(--color-muted)]')
                  }
                >
                  {m.milestoneDate}
                </span>
                <button
                  type="button"
                  onClick={() => void remove(m)}
                  disabled={busy}
                  className="opacity-0 transition group-hover:opacity-100 hover:text-[var(--color-danger)]"
                  title="Delete"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <div className="mt-4 flex gap-2">
        <input
          type="text"
          placeholder="Milestone title (e.g. Kickoff, Phase 1 complete)…"
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void add();
          }}
          disabled={busy}
          className="flex-1 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm outline-none focus:border-amber-600 focus:ring-2 focus:ring-amber-600/30"
        />
        <input
          type="date"
          value={newDate}
          onChange={(e) => setNewDate(e.target.value)}
          required
          disabled={busy}
          title="Milestone date"
          className="rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm outline-none focus:border-amber-600 focus:ring-2 focus:ring-amber-600/30"
        />
        <Button onClick={() => void add()} disabled={busy || !newTitle.trim() || !newDate}>
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      {err && <p className="mt-2 text-sm text-[var(--color-danger)]">{err}</p>}
    </div>
  );
}
